import { AxiosRequestConfig } from 'axios';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Response } from 'api/typegen/client/application/get';
import { useErrorManager } from 'InternalLibrary';
import axiosInstance from 'utils/axiosInstance';
import isLogged from 'utils/isLogged';

type Props = {
  children: ReactNode;
};

type ApplicationContextType = {
  data: Response | undefined | null;
  fetching: boolean;
  fetch: (
    options?: { headers: any } | undefined,
  ) => Promise<Response | undefined>;
};

const ApplicationContext = createContext<ApplicationContextType>(
  {} as ApplicationContextType,
);

const useApplication = (): ApplicationContextType =>
  useContext(ApplicationContext);

const ApplicationProvider = ({ children }: Props): JSX.Element => {
  const [data, setData] = useState<Response | undefined | null>();
  const [fetching, setFetching] = useState(false);
  const { setError } = useErrorManager();

  const fetchApplication = useCallback(
    async (
      options?: AxiosRequestConfig<any> | undefined,
    ): Promise<Response> => {
      try {
        setFetching(true);
        const { data: fetchedClient } = await axiosInstance.get(
          'client/application',
          options,
        );
        setData(fetchedClient);
        return fetchedClient;
      } catch (e) {
        setData(null);
        setError();
        throw e;
      } finally {
        setFetching(false);
      }
    },
    [setError],
  );

  useEffect(() => {
    const prefetch = async () => {
      if (data === undefined && isLogged()) {
        try {
          await fetchApplication({
            headers: {
              Accept: 'application/vnd.4finance.web.v1.hal+json',
              'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
            },
          });
        } catch {
          console.error('Error fetching application');
        }
      }
    };
    prefetch();
  }, []);

  const applicationContextValue = useMemo(
    () => ({
      data,
      fetch: fetchApplication,
      fetching,
    }),
    [data, fetchApplication, fetching],
  );

  return (
    <ApplicationContext.Provider value={applicationContextValue}>
      {children}
    </ApplicationContext.Provider>
  );
};

export { useApplication as default, ApplicationProvider };
